import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HelpCircle, Search, MessageCircle, Phone, Mail, BookOpen, FileText } from 'lucide-react';
import { Input, Button } from '../../components/shared';
import { FAQSkeleton } from '../../components/shared/ClientPageSkeleton';
import { usePublicFAQs } from '../../hooks/usePublicFAQs';
import { siteSettingsService } from '../../services/siteSettingsService';
import { useAuth } from '../../contexts/AuthContext';

const categoryIcons = {
  'getting-started': BookOpen,
  'bookings': FileText,
  'payments': FileText,
  'account': HelpCircle,
  'safety': HelpCircle,
};

const HelpCenter = () => {
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const { faqsByCategory, categories, isLoading, error } = usePublicFAQs();

  const { data: settingsResponse } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });
  const settings = settingsResponse?.data || {};
  const supportEmail = settings.site_email || 'support@example.com';
  const supportPhone = settings.site_phone || '1-800-000-0000';

  const tabs = categories.map((cat) => ({
    ...cat,
    icon: categoryIcons[cat.id] || HelpCircle,
  }));

  const faqs = useMemo(() => {
    const list = faqsByCategory[activeTab] || [];
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (faq) =>
        (faq.question || '').toLowerCase().includes(q) ||
        (faq.answer || '').toLowerCase().includes(q)
    );
  }, [faqsByCategory, activeTab, searchQuery]);

  const handleStartChat = () => {
    window.dispatchEvent(
      new CustomEvent(isAuthenticated ? 'openChatWidget' : 'openGuestConcierge')
    );
  };

  const handleSendEmail = () => {
    window.location.href = `mailto:${supportEmail}`;
  };

  const handleCallNow = () => {
    // Convert vanity numbers (e.g. 1-800-VENDA-LIST) - letters to digits per phone keypad
    const letterToDigit = { A: 2, B: 2, C: 2, D: 3, E: 3, F: 3, G: 4, H: 4, I: 4, J: 5, K: 5, L: 5, M: 6, N: 6, O: 6, P: 7, Q: 7, R: 7, S: 7, T: 8, U: 8, V: 8, W: 9, X: 9, Y: 9, Z: 9 };
    const telNumber = supportPhone
      .split('')
      .map((c) => (/\d/.test(c) ? c : letterToDigit[c.toUpperCase()]))
      .filter((c) => c != null)
      .join('');
    window.location.href = `tel:${telNumber}`;
  };

  const contactOptions = [
    {
      icon: MessageCircle,
      title: 'Live Chat',
      description: 'Chat with our support team',
      action: 'Start Chat',
      onClick: handleStartChat,
    },
    {
      icon: Mail,
      title: 'Email Support',
      description: supportEmail,
      action: 'Send Email',
      onClick: handleSendEmail,
    },
    {
      icon: Phone,
      title: 'Phone Support',
      description: supportPhone,
      action: 'Call Now',
      onClick: handleCallNow,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 -mt-16">
      {/* Hero Section - extends behind transparent header */}
      <div className="bg-gradient-to-br from-black to-gray-900 text-white pt-16 pb-12 sm:pt-20 sm:pb-16">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-white/10 backdrop-blur-sm rounded-full mb-4 sm:mb-6">
              <HelpCircle className="w-6 h-6 sm:w-8 sm:h-8 animate-bounce-gentle" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              Help Center
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8">
              Find answers to common questions and get the support you need
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto px-2">
              <div className="relative">
                <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search for help..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white/10 border-white/20 text-white placeholder-gray-400 focus:bg-white/20"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-container py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Tabs Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 sm:mb-8">
            <div className="border-b border-gray-200">
              <nav className="flex overflow-x-auto -mb-px px-2 sm:px-4 md:px-6 scrollbar-hide">
                {tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`relative py-3 sm:py-4 px-2 sm:px-3 md:px-6 font-medium text-xs sm:text-sm md:text-base transition-all duration-300 whitespace-nowrap border-b-2 flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ${
                        activeTab === tab.id
                          ? "text-black border-black"
                          : "text-gray-600 border-transparent hover:text-black hover:border-gray-300"
                      }`}
                    >
                      <IconComponent className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 flex-shrink-0" />
                      <span className="hidden xs:inline sm:hidden md:inline">{tab.label}</span>
                      <span className="xs:hidden sm:inline md:hidden">{tab.label.split(' ')[0]}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-3 sm:p-4 md:p-6 lg:p-8">
              <div className="space-y-4 sm:space-y-6">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  Frequently Asked Questions
                </h2>
                {isLoading ? (
                  <FAQSkeleton count={6} />
                ) : error ? (
                  <div className="py-8 text-center text-red-500">
                    Failed to load FAQs. Please try again later.
                  </div>
                ) : (
                <div className="space-y-3 sm:space-y-4">
                  {faqs.map((faq, index) => (
                    <div
                      key={faq.id || index}
                      className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-6 hover:border-gray-300 transition-colors"
                    >
                      <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2">
                        {faq.question}
                      </h3>
                      <p className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
                )}
              </div>
            </div>
          </div>

          {/* Contact Support Section */}
          <div className="bg-gradient-to-br from-gray-900 to-black text-white rounded-xl p-4 sm:p-6 md:p-8">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 md:mb-6">Still Need Help?</h2>
            <p className="text-xs sm:text-sm md:text-base text-gray-300 mb-4 sm:mb-6 md:mb-8">
              Our support team is here to help you 24/7
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              {contactOptions.map((option, index) => {
                const IconComponent = option.icon;
                return (
                  <div
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 md:p-6 hover:bg-white/20 transition-colors"
                  >
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 flex-shrink-0" />
                      <h3 className="text-sm sm:text-base md:text-lg font-semibold">{option.title}</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-300 mb-3 sm:mb-4">{option.description}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={option.onClick}
                      className="w-full bg-white text-black hover:bg-gray-100 text-xs sm:text-sm cursor-pointer"
                    >
                      {option.action}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;

